/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {Movement}        from '@/models/scenario/movement';
import {MovementHistory} from '@/models/scenario/movement.history';
import {Train}           from '@/models/scenario/train';
import {Logger}          from '@/util/logger';
import {ViewerInterface} from './viewer.interface';


import * as THREE        from 'three';

import {
  Vector2,
  Box2
}                        from 'three';

import * as $            from 'jquery';

import 'devbridge-autocomplete';

/**
 *
 * @class SearchManager
 *
 * @brief tracks and manages the train histories that match the user's search criteria
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class SearchManager {

  /** @brief _viewerViewerInterface the main viewer of the graph */
  private        _viewer: ViewerInterface = null;

  /* drag resize support */

  private        _startX: number         = 0;
  private        _startY: number         = 0;
  private    _startWidth: number         = 0;
  private   _startHeight: number         = 0;
  private      _dragging: boolean        = false;

  /** @var _focusIndex number pointer to the item we're focused on */
  private    _focusIndex: number          = -1;

  /** @var _pillContainer JQuery the panel where we show the selection pillbox buttons */
  private _pillContainer: JQuery       = null;

  /** @var _button JQuery the button we can expand to auto-complete + button */
  private        _button: JQuery       = null;

  /** @var _searchBase string one big string with everything we can regex on */
  private    _searchBase: string = '';

  /** @var _searchMap Map<number, Train> train ids => Trains */
  private     _searchMap: Map<number, Train> = new Map<number, Train>();

  /** the most recent array of search hits */
  private   _lastMatches: any;

  /** @var _lastSearch the last search string we picked on to get results */
  private    _lastSearch: {
    value: string,
    data: {
      kind: string
    }
  }                                   = null;

  /* the viewer windows have to have uniqu'ified DOM since there may be more than one viewer in the browser */
  private uuid: number = 0;

  /** @brief _search MovementHistory[] the current search results */
  private        _search: {
    focusBound: THREE.Box2,
       history: MovementHistory,
         index: number
  }[]                                 = [];

  /* the complete set of all possible movement histories */
  private           _all: MovementHistory[]       = [];

  /** @var _nightMode boolean are we in printing mode or view mode? */
  private _nightMode: boolean = true;

  /** @brief standard constructor */
  constructor(viewer: ViewerInterface, uuid: number) {

    this._viewer = viewer;
    this.uuid    = uuid;
  }

  /** @brief switch between print and viewing mode */
  nightMode(mode: boolean): void {

    this._nightMode = mode;

    this.updatePanel();
  }

  /** @brief enter resize-drag mode */
  startDrag(e: JQuery, event: MouseEvent): void {

    if (this._dragging) {

      /* already started dragging */

      return;
    }

    this._dragging    = true;

    this._startX      = event.clientX;
    this._startY      = event.clientY;
    this._startWidth  = $(e).width();
    this._startHeight = $(e).height();

  }

  /** @brief drag the panel size out */
  drag(e: JQuery, event: MouseEvent): void {

    if (!this._dragging) {

      /* not in drag mode */

      return;
    }

    const  newWidth: number  = this._startWidth  + this._startX - event.clientX;
    const newHeight: number = this._startHeight + event.clientY - this._startY;

    $(e).width(newWidth);
    $(e).height(newHeight);

  }

  /** @brief stop resizing the panel */
  stopDrag(e: JQuery, event: MouseEvent): void {

    if (!this._dragging) {

      /* not in drag mode */

      return;
    }

    this._dragging    = false;
  }

  history(all: MovementHistory[]) {

    this._all = all;

    /* build the search base */

    for (const m of this._all) {

      const train: Train = m._train;
      let   index: string = '';

      index += train._generatedId + ';';
      index += train._name + ';';
      index += Train.typeToString(train._type) + ';';

      index.toLowerCase();

      this._searchBase += index;

      this._searchMap.set(train._generatedId, train);
    }

  }

  /** @brief given the #id of the action button and its dropdown, setup the resizable dropdown */
  finalize(idOfActionButton: string, idOfDropdown: string) {

    const     e: JQuery = $('#' + idOfDropdown);

    this._pillContainer = e;
    this._button        = $('#' + idOfActionButton);

    /* setup the resize behavior for the drop down panel */

    $(e).append('<div class="resizer-left"></div>');
    $(e).append('<div class="resizer-bottom"></div>');
    $(e).append('<div class="resizer-both-left"></div>');
    $(e).append('<div class="resizer-clear-list"></div>');

    const startFN = (event) => {

      this.startDrag(e, event);

    };

    $( '#' + idOfDropdown + ' .resizer-left' ).on('mousedown', startFN);
    $( '#' + idOfDropdown + ' .resizer-bottom' ).on('mousedown', startFN);
    $( '#' + idOfDropdown + ' .resizer-both-left' ).on('mousedown', startFN);

    const dragFN = (event) => {

      this.drag(e, event);

    };

    document.documentElement.addEventListener('mousemove', dragFN, false);

    const stopFN = (event) => {

      this.stopDrag(e, event);

    };

    document.documentElement.addEventListener('mouseup', stopFN, false);

    $(this._pillContainer).find('.resizer-clear-list').on('click', () => {

      this.clear();
    });
  }

  /** @brief test to see if this one is the one currently focused on */
  isFocus(history: MovementHistory) {

    if ((this._focusIndex < 0) || (this._search.length === 0)) {

      /* there is no focus */

      return false;
    }

    return this._search[this._focusIndex].history._train._generatedId === history._train._generatedId;
  }

  /** @brief show the drop down panel */
  show() {

    $(this._pillContainer).css('display', 'block');
  }

  /** @brief hide the drop down panel */
  hide() {

    $(this._pillContainer).css('display', 'none');
  }

  /** @brief test if the dropdown is visible */
  visible(): boolean {

    return $(this._pillContainer).css('display') !== 'none';
  }

  /** @brief focus user input in the auto-complete input. */
  focus(): void {

    this.hide();

    /* show the auto-complete input */

    let html = `<button class="train-autocomplete-clr-btn" id="train-train-autocomplete-clr-btn_${this.uuid}">CLR</button>`;
    html      += `<button class="train-autocomplete-all-btn" id="train-train-autocomplete-all-btn_${this.uuid}" disabled="true">ALL</button>`;
    html      += `<input type="text" name="train" class="train-autocomplete-input" id="train-autocomplete-input_${this.uuid}"/>`;

    $(this._button).parent().after(html);
    $(`#train-autocomplete-input_${this.uuid}`).focus();

    $(`#train-autocomplete-input_${this.uuid}`).on('keydown', (e) => {

      /* if they hit enter in the search input, it means do the multi-pick */

      if (e.key.toLowerCase() === 'enter') {

        e.preventDefault();
        e.stopPropagation();

        this.multiPick(this._lastMatches);
      }

      /* all them to close the auto-complete from within the auto-complete */

      if ((e.ctrlKey) && ((e.key === 'f') || (e.key === 'F'))) {

        this.unfocus();

        e.preventDefault();

        this._viewer.takeFocus();
      }

      if ((e.key === 'f3') || (e.key === 'F3')) {

        this.unfocus();

        this.show();

        this._viewer.takeFocus();

        e.preventDefault();
      }

      if ((e.key === 'tab') || (e.key === 'Tab')) {

        e.preventDefault();
      }

    });

    /* you can always clear the search */

    $(`.train-autocomplete-clr-btn`).unbind('click').on('click', (e) => {

      this.multiPick([]);
    });

    /*
     * give the input auto-complete ability...
     *
     *   @link https://www.devbridge.com/sourcery/components/jquery-autocomplete/
     *   @link https://www.npmjs.com/package/devbridge-autocomplete
     *
     */

    $(`#train-autocomplete-input_${this.uuid}`).devbridgeAutocomplete ({
      lookup: (query, done) => {

        done(this.match(query));
      },
      groupBy: 'kind',
      onSelect: (suggestion) => {
        this.pick(suggestion);
      }
    });

  }

  /** @brief auto-complete method */
  match(query) {

    /*
     * regexp the search base to find the matches ...
     *
     * search base is a big string of entries:
     *
     *   <train id> ; <train name> ; <train type>
     *
     * We aren't going absolutely unique on matching, just match train name + train kind;
     * when they pick one, we will expand to all instances of that named train and kind.
     *
     */

    Logger.debug('[search]', 'matching: ', query);

    /* build hte regex to use... */

    query = query.replace(/\.\*/g, '[^;]*');
    query = query.replace(/\.\+/g, '[^;]+');

    this._lastMatches = [];

    Logger.debug('[search] matching: ', query);

    const     re: RegExp      = new RegExp('([0-9]+);((?:(?!;)'  + query + '));([a-z]+);', 'gi');
    const unique: Set<string> = new Set<string>();
    const   hits: {value: string, data: {kind: string}}[] = [];
    let   result: any         = null;

    while (true) {

      result = re.exec(this._searchBase);

      if (!result) {

        break;
      }

      if (unique.has(result[2] + result[3])) {

        /* already have this train name for this kind of train */

        continue;
      }

      hits.push({
        value: result[2],
        data: {
          kind: result[3]
        }
      });

      unique.add(result[2] + result[3]);
    }

    /* sort the hits array before we pass everything back */

    hits.sort((a, b): number => {

      return a.value.localeCompare(b.value);

    });

    this._lastMatches = hits;

    /* enable/disable the ALL button if we got some or no matches */

    if (hits.length == 0) {

      $(`.train-autocomplete-all-btn`).prop('disabled', true);

    } else {

      $(`.train-autocomplete-all-btn`).prop('disabled', false);
      $(`.train-autocomplete-all-btn`).unbind('click').on('click', (e) => {

        this.multiPick(this._lastMatches);
      });
    }

    return {suggestions: hits};
  }

  /** helper to pick all current matches instead of just one. */
  multiPick(matches) {

    if (matches.length == 0) {

      /* just clear */

      Logger.debug('[search] multi-pick: clearing.');

      this.clear();

      return ;
    }

    Logger.debug('[search] multi-pck: ', matches);

    /* go through the histories, and grab all that match the name/type of train ... */

    this._lastSearch = {
      value: '*',
      data: {
        kind: '*'
      }
    };

    this._search     = [];


    for (const history of this._all) {

      for (const match of matches) {

        if ((history._train._name !== match.value) ||
          (Train.typeToString(history._train._type) !== match.data.kind)) {

          /* filtered */

          continue;
        }


        let minX: number = Number.POSITIVE_INFINITY;
        let minY: number = Number.POSITIVE_INFINITY;
        let maxX: number = Number.NEGATIVE_INFINITY;
        let maxY: number = Number.NEGATIVE_INFINITY;

        for (let j = 0; j < history._history.length; j++) {

          if (j >= 5) {

            /* just use the first 5 moves as the bounding box to focus on */

            break;
          }

          minX = Math.min(minX, history._tMin / 3600.0 * this._viewer.timeScale());
          minY = Math.min(minY, history._dMin * this._viewer.distanceScale());
          maxX = Math.max(maxX, history._tMax / 3600.0 * this._viewer.timeScale());
          maxY = Math.max(maxY, history._dMax * this._viewer.distanceScale());

        }

        /*
         * figure out a bounding box we can use to focus on the match, for now,
         * we just use the first 5 moves of the train as the bounding box to
         * focus on.
         *
         */

        /*
        let offsetX: number = this._viewer.dataX() * this._viewer.timeScale();
        let offsetY: number = this._viewer.dataY() * this._viewer.distanceScale();
        */

        const bound: Box2 = new THREE.Box2(new Vector2(minX, minY), new Vector2(maxX, maxY));

        /* save this instance of the train */

        this._search.push({
          focusBound: bound,
          history,
          index: this._search.length
        });

      } /* end of considering each match */

    } /* end of all trains walk */

    /* sort the results by departure time */

    this._search.sort((a: {
      focusBound: Box2,
      history: MovementHistory,
      index: number
    },         b: {
      focusBound: Box2,
      history: MovementHistory,
      index: number
    }): number => {

      const depA: number = a.history._history[0]._departure;
      const depB: number = b.history._history[0]._departure;

      return depA - depB;
    });

    this._focusIndex = -1;

    /* update the search panel */

    this.updatePanel();

    /* tell the viewer to re-draw the search matches */

    this._viewer.setSearch(this.search());

    /*
     * if we had a search hit, then focus on the first one, so that the user
     * doesn't have to go hunting for the thing they just searched for.
     *
     */

    if (this._search.length) {

      this._viewer.focusCamera(this._search[0].focusBound);
    }

    this.unfocus();
  }

  /** @brief auto-complete "success" (they chose an item) */
  pick(suggestion: {
      value: string,
      data: {
        kind: string
      }
    }) {

    Logger.debug('[search]', 'picked: ', suggestion);

    /* go through the histories, and grab all that match the name/type of train ... */

    this._lastSearch = suggestion;
    this._search     = [];

    for (const history of this._all) {

      if ((history._train._name !== suggestion.value) ||
          (Train.typeToString(history._train._type) !== suggestion.data.kind)) {

        /* filtered */

        continue;
      }


      let minX: number = Number.POSITIVE_INFINITY;
      let minY: number = Number.POSITIVE_INFINITY;
      let maxX: number = Number.NEGATIVE_INFINITY;
      let maxY: number = Number.NEGATIVE_INFINITY;

      for (let j = 0; j < history._history.length; j++) {

        if (j >= 5) {

          /* just use the first 5 moves as the bounding box to focus on */

          break;
        }

        minX = Math.min(minX, history._tMin / 3600.0 * this._viewer.timeScale());
        minY = Math.min(minY, history._dMin * this._viewer.distanceScale());
        maxX = Math.max(maxX, history._tMax / 3600.0 * this._viewer.timeScale());
        maxY = Math.max(maxY, history._dMax * this._viewer.distanceScale());

      }

      /*
       * figure out a bounding box we can use to focus on the match, for now,
       * we just use the first 5 moves of the train as the bounding box to
       * focus on.
       *
       */

      /*
      let offsetX: number = this._viewer.dataX() * this._viewer.timeScale();
      let offsetY: number = this._viewer.dataY() * this._viewer.distanceScale();
      */

      const bound: Box2 = new THREE.Box2(new Vector2(minX, minY), new Vector2(maxX, maxY));

      /* save this instance of the train */

      this._search.push({
        focusBound: bound,
            history,
             index: this._search.length
      });

    }

    /* sort the results by departure time */

    this._search.sort((a: {
      focusBound: Box2,
         history: MovementHistory,
           index: number
    },         b: {
      focusBound: Box2,
         history: MovementHistory,
           index: number
    }): number => {

      const depA: number = a.history._history[0]._departure;
      const depB: number = b.history._history[0]._departure;

      return depA - depB;
    });

    this._focusIndex = -1;

    /* update the search panel */

    this.updatePanel();

    /* tell the viewer to re-draw the search matches */

    this._viewer.setSearch(this.search());

    /*
     * if we had a search hit, then focus on the first one, so that the user
     * doesn't have to go hunting for the thing they just searched for.
     *
     */

    if (this._search.length) {

      this._viewer.focusCamera(this._search[0].focusBound);
    }

    this.unfocus();
  }

  /** @brief clear out the current search results */
  clear(): void {

    Logger.debug('[search]', 'trashing search results...');

    this._search     = [];
    this._lastSearch = null;
    this._focusIndex = -1;

    this._viewer.setSearch(this.search());

    this.updatePanel();

    this.hide();

    this.unfocus();
  }

  /** @brief fetch the current search results */
  search(): MovementHistory[] {

    return this._search.map(s => s.history);
  }

  /** @brief give us the focus on the auto-complete input */
  unfocus(): void {

    /* hide the auto-complete input */

    $(`#train-autocomplete-input_${this.uuid}`).devbridgeAutocomplete('dispose');
    $(`#train-autocomplete-input_${this.uuid}`).remove();
    $(`.train-autocomplete-all-btn`).remove();
    $(`.train-autocomplete-clr-btn`).remove();

    this._viewer.takeFocus();
  }

  /** @brief re-populate the search drop down results panel, based on the current results */
  updatePanel(): void {

    $(this._pillContainer).find('.search-result-header').remove();
    $(this._pillContainer).find('.search-result-scroller').remove();

    if (this._search.length === 0) {

      /* nothing to show */

      return ;
    }

    let html: string            = '';

    html                      += '<span class="search-result-header ' + (this._nightMode ? ' night ' : ' day ') + '">' +
                                 this._lastSearch.value + ' (' +
                                 this._lastSearch.data.kind.toUpperCase() + ')</span>';

    html                      += '<ul class="search-result-scroller">';

    $(this._pillContainer).find('.search-result.focused').removeClass('focused');

    for (const item of this._search) {

      const   moves: MovementHistory = item.history;
      const      id: number          = moves._train._generatedId;
      const  depart: number          = moves._history[0]._departure;
      let   classes: string          = (item.index === this._focusIndex) ? 'focused' : '';
      let      attr: string          = '';
      attr                          += ' data-id="' + id + '"';

      if (this._nightMode) {

        classes += ' night ';

      } else {

        classes += ' day ';
      }

      const timeofday: string          = new Date(depart * 1000).toISOString().substr(11, 8);
      const       day: string          = Math.floor(depart / 86400).toString().padStart(2, '0');
      const     label: string          = 'DAY ' + day + ' ' + timeofday + ' (' + moves._history[0]._dp._name + ' ' +
                                         moves._history[0]._dp._mileage  + ' mi)';

      html                       += '<li class="search-result ' + classes + '" ' + attr + '>';
      html                       += label;
      html                       += '</li>';
    }

    html                      += '</ul>';

    $(this._pillContainer).append(html);

    setTimeout(() => {

      if (this._focusIndex > -1) {

        $(this._pillContainer).find('.search-result-scroller').scrollTop($('.search-result.focused').position().top - 48);
      }

    }, 100);

    /* add the behaviors ... */

    $(this._pillContainer).find('.search-result').on('click', (e) => {

      /* fetch train id from the element they clicked on */

      const trainId = parseInt($(e.target).data('id'), 10);

      /* find the item */

      const items = this._search.filter(s => s.history._train._generatedId === trainId);

      /* view it */

      if (items.length > 0) {

        this._viewer.focusCamera(items[0].focusBound);

        this._focusIndex = items[0].index;

        this.updatePanel();

        this._viewer.setSearch(this.search());
      }

    });

  }

  /** @brief test if we're already showing the search auto-complete */
  alreadyInFccus(): boolean {


    if ($(`#train-autocomplete-input_${this.uuid}`).length > 0) {

      return true;
    }

    return false;

  }

  /** @brief focus on the previous selection item */
  focusPrevious(): void {

    this.show();

    if (this._search.length === 0) {

      /* nothing to do */

      return ;
    }

    if (this._focusIndex < 0) {
      this._focusIndex = 0;
    }

    this._focusIndex--;

    if (this._focusIndex < 0) {

      this._focusIndex = this._search.length - 1;
    }

    this._viewer.setSearch(this.search());

    this.updatePanel();

    this._viewer.focusCamera(this._search[this._focusIndex].focusBound);
  }

  /** @brief focus on the next selection item */
  focusNext(): void {

    this.show();

    if (this._search.length === 0) {

      /* nothing to do */

      return ;
    }

    if (this._focusIndex < 0) {
      this._focusIndex = 0;
    }

    this._focusIndex++;

    if (this._focusIndex >= this._search.length) {

      this._focusIndex = 0;
    }

    this._viewer.setSearch(this.search());

    this.updatePanel();

    this._viewer.focusCamera(this._search[this._focusIndex].focusBound);

  }
}
